import classNames from "classnames";
import map from "lodash/map";
import uniq from "lodash/uniq";
import React, { FunctionComponent, useMemo, useRef } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../../utils/customization/finders";
import { RotationPagesMappingCollection } from "../../../../utils/customization/page";
import MockPage from "../MockPage";
import "./style.scss";

const MAX_LAYOUT_SIZE = 56;

interface Props {
  currentPageDefinitionKey?: string;
  layoutType?: "page" | "cover";
  pageKey?: string;
  rotatedPairs?: RotationPagesMappingCollection;
  undoKey?: string;
}

const PageLayouts: FunctionComponent<Props> = ({
  currentPageDefinitionKey,
  layoutType = "page",
  pageKey,
  rotatedPairs,
  undoKey,
}) => {
  const { content, dispatch, template } = useCustomizationContext();
  const pageLayoutsElement = useRef<HTMLDivElement>(null);

  const filteredPageDefinitions = useMemo(() => {
    const filtered = findPageDefinitionsWithValidCondition(template, content);
    if (layoutType === "page" && filtered) {
      return getNoCoverDefinitions(filtered);
    }
    return filtered;
  }, [content, layoutType, template]);

  let selectedPageKey = "";
  if (currentPageDefinitionKey) {
    selectedPageKey = currentPageDefinitionKey;
  } else {
    const pageDefinitionKeys = uniq(
      map(content.pages, ({ pageDefinitionKey }) => pageDefinitionKey)
    );
    if (pageDefinitionKeys.length === 1) {
      selectedPageKey = pageDefinitionKeys[0];
    }
  }

  let onClick: (pageDefinition: TemplatePageDefinition) => void;
  if (pageKey) {
    onClick = (pageDefinition: TemplatePageDefinition) =>
      dispatch({
        pageDefinitionKey: pageDefinition.key,
        pageKey,
        type: "changePagePageDefinition",
        undoKey,
      });
  } else {
    onClick = (pageDefinition: TemplatePageDefinition) => {
      dispatch({
        pageDefinitionKey: pageDefinition.key,
        type: "changeAllPagesPageDefinition",
        undoKey,
      });
    };
  }

  let currentPairOrientation: "rotated" | "non-rotated" = "non-rotated";
  if (currentPageDefinitionKey && rotatedPairs) {
    const currentRotationPair = rotatedPairs[currentPageDefinitionKey];
    if (currentRotationPair) {
      currentPairOrientation = currentRotationPair.which;
    }
  }

  let currentPageDefinition: TemplatePageDefinition | undefined;
  if (currentPageDefinitionKey) {
    currentPageDefinition = findPageDefinition(currentPageDefinitionKey, template);
  }

  const tmpPageLayouts = map(filteredPageDefinitions, (pageDefinition) => {
    if (
      currentPageDefinition &&
      (currentPageDefinition.type !== pageDefinition.type ||
        currentPageDefinition.filterType !== pageDefinition.filterType)
    ) {
      return null;
    }
    if (
      rotatedPairs &&
      currentPairOrientation &&
      rotatedPairs[pageDefinition.key] &&
      rotatedPairs[pageDefinition.key].which !== currentPairOrientation
    ) {
      return null;
    }
    return (
      <div
        key={pageDefinition.key}
        className={classNames("PageLayouts-item", {
          "PageLayouts-item--selected": pageDefinition.key === selectedPageKey,
        })}
        onClick={() => onClick(pageDefinition)}
      >
        <MockPage
          pageDefinitionKey={pageDefinition.key}
          restrictedSize={MAX_LAYOUT_SIZE}
          selected={pageDefinition.key === selectedPageKey}
        />
      </div>
    );
  });

  return (
    <div className="PageLayouts" ref={pageLayoutsElement}>
      {tmpPageLayouts}
    </div>
  );
};

export default PageLayouts;
